import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useXP } from '../../contexts/XPContext';
import { apiEndpoints } from '../../api/client';
import AccountSettingsModal from '../AccountSettingsModal';
import { CREDITS_UPDATED_EVENT } from '../CreditDisplay';
import CareerlyLogo from '../../assets/CareerlyLogo.png';

// Icons as inline SVGs for the navigation
const Icons = {
    dashboard: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" rx="1"/>
            <rect x="14" y="3" width="7" height="7" rx="1"/>
            <rect x="3" y="14" width="7" height="7" rx="1"/>
            <rect x="14" y="14" width="7" height="7" rx="1"/>
        </svg>
    ),
    discover: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
        </svg>
    ),
    resume: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
    ),
    interview: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
    ),
    quiz: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
        </svg>
    ),
    progress: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="20" x2="18" y2="10"/>
            <line x1="12" y1="20" x2="12" y2="4"/>
            <line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
    ),
    profile: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
        </svg>
    ),
    star: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
    ),
    logout: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
    ),
    settings: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
    ),
    notification: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
    ),
    crown: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7zM3 20h18"/>
        </svg>
    ),
    location: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
        </svg>
    ),
    chevronLeft: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"/>
        </svg>
    ),
    chevronRight: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6"/>
        </svg>
    ),
};

// Close icon for mobile drawer
const CloseIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);

const Sidebar = ({ userData }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout, user, userProfile } = useAuth();
    const { isCollapsed, toggleSidebar, isMobile, isMobileOpen, closeMobileMenu } = useSidebar();
    const [hoveredItem, setHoveredItem] = useState(null);
    const [logoutHovered, setLogoutHovered] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [settingsHovered, setSettingsHovered] = useState(false);
    const [notificationHovered, setNotificationHovered] = useState(false);
    const [isPremium, setIsPremium] = useState(false);
    const [collapseHovered, setCollapseHovered] = useState(false);

    // Close mobile menu on route change
    useEffect(() => {
        if (isMobile && isMobileOpen) {
            closeMobileMenu();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Fetch premium status
    const fetchPremiumStatus = useCallback(async () => {
        try {
            const data = await apiEndpoints.auth.getCredits();
            setIsPremium(data?.is_premium || false);
        } catch (error) {
            console.error('Error fetching premium status:', error);
        }
    }, []);

    useEffect(() => {
        fetchPremiumStatus();

        // Listen for credit updates to refresh premium status
        const handleCreditsUpdated = () => {
            fetchPremiumStatus();
        };
        window.addEventListener(CREDITS_UPDATED_EVENT, handleCreditsUpdated);
        return () => window.removeEventListener(CREDITS_UPDATED_EVENT, handleCreditsUpdated);
    }, [fetchPremiumStatus]);

    const getDisplayName = () => {
        // First check userProfile (from onboarding), then OAuth metadata, then email
        if (userProfile?.full_name) {
            return userProfile.full_name;
        }
        if (user?.user_metadata?.full_name) {
            return user.user_metadata.full_name;
        }
        const email = user?.email || '';
        return email.split('@')[0];
    };

    const getInitials = () => {
        const name = getDisplayName();
        return name.charAt(0).toUpperCase();
    };

    const navItems = [
        { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: 'dashboard' },
        { id: 'profile', label: 'Profile', path: '/profile', icon: 'profile' },
        { id: 'discover-jobs', label: 'Discover Jobs', path: '/discover-jobs', icon: 'discover' },
        { id: 'generate-resumes', label: 'Generate Resumes', path: '/generate-resumes', icon: 'resume' },
        { id: 'interview-prep', label: 'Interview Prep', path: '/interview-prep', icon: 'interview' },
        { id: 'quiz-practice', label: 'Quiz Practice', path: '/quiz-practice', icon: 'quiz' },
        { id: 'progress', label: 'Progress', path: '/progress', icon: 'progress' },
    ];

    const getCurrentPage = () => {
        const path = location.pathname;
        const item = navItems.find(item => path.startsWith(item.path));
        return item?.id || 'dashboard';
    };

    const currentPage = getCurrentPage();

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    // Get XP data from context
    const { xpStatus } = useXP();

    // User level data from XP context (with fallbacks for loading state)
    const level = xpStatus?.level || 1;
    const levelTitle = xpStatus?.level_title || 'Job Seeker';
    const currentXP = xpStatus?.current_xp || 0;
    const nextLevelXP = xpStatus?.next_level_xp || 200;
    const xpProgress = xpStatus?.progress_percent || 0;

    // Determine if we should show full sidebar (not collapsed) - always true on mobile
    const showFullSidebar = isMobile || !isCollapsed;

    const styles = {
        // Mobile backdrop overlay
        backdrop: {
            display: isMobile && isMobileOpen ? 'block' : 'none',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            zIndex: 999,
        },
        sidebar: {
            width: isMobile ? '280px' : 'var(--sidebar-width)',
            background: 'var(--bg-card)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRight: '1px solid var(--border-subtle)',
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100dvh', // Use dvh for iOS Safari compatibility
            zIndex: isMobile ? 1000 : 100,
            // GPU-accelerated animation for mobile drawer
            transform: isMobile
                ? (isMobileOpen ? 'translateX(0)' : 'translateX(-100%)')
                : 'translateX(0)',
            transition: 'transform var(--duration-base) var(--ease-out), width var(--duration-base) var(--ease-out)',
        },
        // Mobile close button
        mobileCloseBtn: {
            display: isMobile ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            background: 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            transition: 'all var(--duration-fast)',
        },
        header: {
            padding: showFullSidebar ? 'var(--space-xl)' : 'var(--space-lg) var(--space-sm)',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: showFullSidebar ? 'space-between' : 'center',
        },
        logo: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            textDecoration: 'none',
            cursor: 'pointer',
            justifyContent: showFullSidebar ? 'flex-start' : 'center',
        },
        logoMark: {
            width: '36px',
            height: '36px',
            flexShrink: 0,
        },
        logoText: {
            fontFamily: 'var(--font-display)',
            fontSize: '1.5rem',
            fontWeight: 600,
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            display: showFullSidebar ? 'block' : 'none',
        },
        collapseBtn: {
            background: collapseHovered ? 'var(--bg-surface)' : 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--space-xs)',
            cursor: 'pointer',
            color: collapseHovered ? 'var(--text-primary)' : 'var(--text-tertiary)',
            transition: 'all var(--duration-fast)',
            // Hide on mobile (we use close button instead) and when collapsed
            display: (isMobile || isCollapsed) ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        nav: {
            flex: 1,
            padding: showFullSidebar ? 'var(--space-lg) var(--space-md)' : 'var(--space-lg) var(--space-xs)',
            overflowY: 'auto',
        },
        navItem: (isActive, isHovered) => ({
            display: 'flex',
            alignItems: 'center',
            gap: showFullSidebar ? 'var(--space-md)' : 0,
            padding: showFullSidebar ? 'var(--space-md) var(--space-lg)' : 'var(--space-md)',
            borderRadius: 'var(--radius-md)',
            color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
            textDecoration: 'none',
            fontWeight: 500,
            transition: 'all var(--duration-base) var(--ease-out)',
            marginBottom: 'var(--space-xs)',
            position: 'relative',
            cursor: 'pointer',
            justifyContent: showFullSidebar ? 'flex-start' : 'center',
            minHeight: 'var(--touch-target)', // Ensure 44px touch target
            background: isActive
                ? 'rgba(99, 102, 241, 0.15)'
                : isHovered
                    ? 'rgba(99, 102, 241, 0.1)'
                    : 'transparent',
        }),
        navItemBar: (isActive) => ({
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '3px',
            height: isActive ? '24px' : 0,
            background: 'var(--gradient-primary)',
            borderRadius: '0 2px 2px 0',
            transition: 'height var(--duration-base) var(--ease-out)',
        }),
        navIcon: {
            width: '22px',
            height: '22px',
            flexShrink: 0,
        },
        footer: {
            padding: showFullSidebar ? 'var(--space-lg)' : 'var(--space-md) var(--space-xs)',
            borderTop: '1px solid var(--border-subtle)',
        },
        levelContainer: {
            display: showFullSidebar ? 'flex' : 'none',
            alignItems: 'center',
            gap: 'var(--space-md)',
            padding: 'var(--space-md)',
            background: 'var(--bg-surface)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-md)',
        },
        levelBadge: {
            width: '44px',
            height: '44px',
            background: 'var(--gradient-warm)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        levelNumber: {
            fontFamily: 'var(--font-display)',
            fontSize: '1.25rem',
            fontWeight: 700,
            color: 'white',
        },
        levelInfo: {
            flex: 1,
            minWidth: 0,
        },
        levelTitle: {
            display: 'block',
            fontWeight: 600,
            fontSize: '0.9rem',
            marginBottom: 'var(--space-xs)',
            color: 'var(--text-primary)',
        },
        xpBar: {
            height: '6px',
            background: 'var(--bg-deep)',
            borderRadius: 'var(--radius-full)',
            overflow: 'hidden',
            marginBottom: 'var(--space-xs)',
        },
        xpFill: {
            height: '100%',
            background: 'var(--gradient-warm)',
            borderRadius: 'inherit',
            transition: 'width var(--duration-slow) var(--ease-out)',
            width: `${xpProgress}%`,
        },
        xpText: {
            fontSize: '0.75rem',
            color: 'var(--text-tertiary)',
        },
        upgradeCta: {
            display: showFullSidebar ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-sm)',
            padding: 'var(--space-md)',
            background: 'var(--gradient-primary)',
            borderRadius: 'var(--radius-md)',
            color: 'white',
            textDecoration: 'none',
            fontWeight: 600,
            transition: 'all var(--duration-base) var(--ease-out)',
            boxShadow: 'var(--glow-indigo)',
            cursor: 'pointer',
            border: 'none',
            width: '100%',
            fontFamily: 'var(--font-body)',
            fontSize: '0.95rem',
            minHeight: 'var(--touch-target)', // Touch target
        },
        logoutBtn: (isHovered) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: showFullSidebar ? 'flex-start' : 'center',
            gap: showFullSidebar ? 'var(--space-md)' : 0,
            padding: showFullSidebar ? 'var(--space-sm) var(--space-md)' : 'var(--space-sm)',
            marginTop: 'var(--space-md)',
            background: isHovered ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            color: isHovered ? 'var(--red-500)' : 'var(--text-tertiary)',
            cursor: 'pointer',
            transition: 'all var(--duration-fast)',
            fontFamily: 'var(--font-body)',
            fontSize: '0.875rem',
            fontWeight: 500,
            width: '100%',
            minHeight: 'var(--touch-target)', // Touch target
        }),
        logoutIcon: {
            width: '18px',
            height: '18px',
            flexShrink: 0,
        },
        bottomActions: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: showFullSidebar ? 'flex-start' : 'center',
            gap: 'var(--space-sm)',
            marginBottom: 'var(--space-md)',
            flexDirection: showFullSidebar ? 'row' : 'column',
        },
        notificationBtn: (isHovered) => ({
            position: 'relative',
            background: isHovered ? 'var(--bg-surface)' : 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 'var(--space-sm)',
            borderRadius: 'var(--radius-md)',
            transition: 'all var(--duration-fast)',
            color: isHovered ? 'var(--text-primary)' : 'var(--text-secondary)',
        }),
        notificationDot: {
            position: 'absolute',
            top: '4px',
            right: '4px',
            width: '8px',
            height: '8px',
            background: 'var(--amber-500)',
            borderRadius: '50%',
            border: '2px solid var(--bg-deep)',
        },
        settingsBtn: (isHovered) => ({
            flex: showFullSidebar ? 1 : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: showFullSidebar ? 'flex-start' : 'center',
            gap: showFullSidebar ? 'var(--space-md)' : 0,
            padding: 'var(--space-md)',
            background: isHovered ? 'var(--bg-surface)' : 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            color: isHovered ? 'var(--text-primary)' : 'var(--text-secondary)',
            cursor: 'pointer',
            transition: 'all var(--duration-fast)',
            fontFamily: 'var(--font-body)',
            fontSize: '0.9rem',
            fontWeight: 500,
            minHeight: 'var(--touch-target)', // Touch target
        }),
        profileAvatar: {
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'var(--gradient-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: '0.8rem',
            color: 'var(--text-primary)',
            flexShrink: 0,
        },
        proBadge: {
            display: showFullSidebar ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-sm)',
            padding: 'var(--space-md)',
            background: 'linear-gradient(135deg, var(--amber-500) 0%, var(--amber-400) 100%)',
            borderRadius: 'var(--radius-md)',
            color: '#000',
            fontWeight: 700,
            fontSize: '0.9rem',
            width: '100%',
        },
        proBadgeIcon: {
            width: '18px',
            height: '18px',
        },
        expandBtn: {
            // Hide on mobile, show only when collapsed on desktop
            display: (!isMobile && isCollapsed) ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            margin: '0 auto var(--space-md)',
            background: collapseHovered ? 'var(--bg-surface)' : 'transparent',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            color: collapseHovered ? 'var(--text-primary)' : 'var(--text-tertiary)',
            transition: 'all var(--duration-fast)',
        },
    };

    return (
        <>
            {/* Mobile backdrop overlay */}
            <div
                style={styles.backdrop}
                onClick={closeMobileMenu}
                aria-hidden="true"
            />

            <aside style={styles.sidebar}>
                {/* Header with Logo */}
                <div style={styles.header}>
                    <div style={styles.logo} onClick={() => navigate('/dashboard')}>
                        <img
                            src={CareerlyLogo}
                            alt="Careerly"
                            style={{
                                height: '36px',
                                width: 'auto',
                                objectFit: 'contain'
                            }}
                        />
                    </div>

                    {/* Mobile close button */}
                    <button
                        style={styles.mobileCloseBtn}
                        onClick={closeMobileMenu}
                        title="Close menu"
                    >
                        <div style={{ width: '24px', height: '24px' }}>
                            {CloseIcon}
                        </div>
                    </button>

                    {/* Desktop collapse toggle button */}
                    <button
                        style={styles.collapseBtn}
                        onClick={toggleSidebar}
                        onMouseEnter={() => setCollapseHovered(true)}
                        onMouseLeave={() => setCollapseHovered(false)}
                        title="Collapse sidebar"
                    >
                        <div style={{ width: '18px', height: '18px' }}>
                            {Icons.chevronLeft}
                        </div>
                    </button>
                </div>

            {/* Navigation */}
            <nav style={styles.nav}>
                {navItems.map(item => {
                    const isActive = currentPage === item.id;
                    const isHovered = hoveredItem === item.id;

                    return (
                        <div
                            key={item.id}
                            style={styles.navItem(isActive, isHovered)}
                            onClick={() => navigate(item.path)}
                            onMouseEnter={() => setHoveredItem(item.id)}
                            onMouseLeave={() => setHoveredItem(null)}
                            title={!showFullSidebar ? item.label : undefined}
                        >
                            <div style={styles.navItemBar(isActive)} />
                            <div style={styles.navIcon}>
                                {Icons[item.icon]}
                            </div>
                            {showFullSidebar && <span>{item.label}</span>}
                        </div>
                    );
                })}
            </nav>

            {/* Footer with Level & Account */}
            <div style={styles.footer}>
                {/* Expand Button (shown when collapsed) */}
                <button
                    style={styles.expandBtn}
                    onClick={toggleSidebar}
                    onMouseEnter={() => setCollapseHovered(true)}
                    onMouseLeave={() => setCollapseHovered(false)}
                    title="Expand sidebar"
                >
                    <div style={{ width: '18px', height: '18px' }}>
                        {Icons.chevronRight}
                    </div>
                </button>

                {/* User Level */}
                <div style={styles.levelContainer}>
                    <div style={styles.levelBadge}>
                        <span style={styles.levelNumber}>{level}</span>
                    </div>
                    <div style={styles.levelInfo}>
                        <span style={styles.levelTitle}>{levelTitle}</span>
                        <div style={styles.xpBar}>
                            <div style={styles.xpFill} />
                        </div>
                        <span style={styles.xpText}>{currentXP.toLocaleString()} / {nextLevelXP.toLocaleString()} XP</span>
                    </div>
                </div>

                {/* Pro Badge or Upgrade CTA */}
                {isPremium ? (
                    <div style={styles.proBadge}>
                        <div style={styles.proBadgeIcon}>
                            {Icons.crown}
                        </div>
                        <span>Pro Member</span>
                    </div>
                ) : (
                    <button
                        style={styles.upgradeCta}
                        onClick={() => navigate('/upgrade')}
                        onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 0 50px rgba(99, 102, 241, 0.4)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'var(--glow-indigo)';
                        }}
                    >
                        <div style={{ width: '18px', height: '18px' }}>
                            {Icons.star}
                        </div>
                        <span>Upgrade to Pro</span>
                    </button>
                )}

                {/* Account Settings & Notifications Row */}
                <div style={styles.bottomActions}>
                    {/* Notification Bell */}
                    <button
                        style={styles.notificationBtn(notificationHovered)}
                        onMouseEnter={() => setNotificationHovered(true)}
                        onMouseLeave={() => setNotificationHovered(false)}
                        title="Notifications"
                    >
                        <div style={{ width: '20px', height: '20px' }}>
                            {Icons.notification}
                        </div>
                        <span style={styles.notificationDot} />
                    </button>

                    {/* Account Settings */}
                    <button
                        style={styles.settingsBtn(settingsHovered)}
                        onClick={() => setShowSettings(true)}
                        onMouseEnter={() => setSettingsHovered(true)}
                        onMouseLeave={() => setSettingsHovered(false)}
                        title={!showFullSidebar ? 'Account Settings' : undefined}
                    >
                        <div style={styles.profileAvatar}>
                            {getInitials()}
                        </div>
                        {showFullSidebar && <span>Account</span>}
                    </button>
                </div>

                {/* Logout Button */}
                <button
                    style={styles.logoutBtn(logoutHovered)}
                    onClick={handleLogout}
                    onMouseEnter={() => setLogoutHovered(true)}
                    onMouseLeave={() => setLogoutHovered(false)}
                    title={!showFullSidebar ? 'Log Out' : undefined}
                >
                    <div style={styles.logoutIcon}>
                        {Icons.logout}
                    </div>
                    {showFullSidebar && <span>Log Out</span>}
                </button>
            </div>

            {/* Account Settings Modal */}
            <AccountSettingsModal
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
            />
        </aside>
        </>
    );
};

export default Sidebar;
